import { Injectable } from 'angular-ts-decorators';

import type { IConfig } from './config';

/**
 * # domain
 * Service in the sharedModule.
 * It takes care of figuring out proper domain for different purposes.
 */
@Injectable('domain')
export class Domain {
  /**
   * @deprecated
   * Alias for {@link sharedModule.domain#createSibling createSibling}
   */
  public sibling: typeof Domain.prototype.createSibling;

  constructor(
    private $location: ng.ILocationService,
    private config: IConfig,
  ) {
    'ngInject';

    this.sibling = this.createSibling;
  }

  /**
   * Get main application domain based on the current. For x.y.nl returns y.nl.
   * @return {string}
   * parent domain
   */
  getParent(): string {
    // TODO consider caching the value
    return this.$location.host().split('.').splice(1).join('.');
  }

  /**
   * Gets the lowest level of the current domain
   * @return
   * sud-domain
   */
  getSubdomain(): string {
    // TODO consider caching the value
    return this.$location.host().split('.')[0];
  }

  /**
   * Gets the current app
   * @return
   * sud-domain
   */
  getApp(): string {
    const match = this.$location.absUrl().match(/([^/]*)\.html#/);

    if (match) {
      return match[1];
    }
    return undefined;
  }

  /**
   * Get request ready spynl domain
   * @return
   * spynl domain
   */
  getSpynl(): string {
    return '//' + this.createSibling(this.config.spynlSubdomain);
  }

  /**
   * Get request ready spynl service domain
   * @return {string}
   * spynl service domain
   */
  getSpynlService(): string {
    return '//' + this.sibling(this.config.spynlServices);
  }

  /**
   * Creats sybling domain - replace last level domain with sibling name. When
   * run at http://docs.dev.swcloud.nl/ with spynl as argument will return
   * **spynl.dev.swcloud.nl**.
   * @param {string} name
   * sibling name
   * @param {boolean} includeProtocol
   * flag set to true if we need sybling with protocol url part included
   * @return {string}
   * sibling domain with the protocol (if it's a case)
   */
  createSibling(name: string, includeProtocol?: boolean): string {
    const prefix = includeProtocol ? this.$location.protocol() + '://' : '';
    return prefix + name + '.' + this.getParent();
  }

  /**
   * Checks if provided url is a sibling domain
   * @param {string} url
   * route to be checked
   * @return {boolean}
   * True if on sibling, false otherwise
   */
  isSibling(url: string): boolean {
    const parentDomain = this.getParent();

    try {
      // we don't care about right protocol, we just want relative url to be
      // parsed in some way
      const parsedUrl = new URL(url, 'http://example.com');

      return parsedUrl.host.endsWith(parentDomain);
    } catch (error) {
      return false;
    }
  }

  /**
   * another service. This is useful, since we operate on
   * many different base domains. So we need some way to
   * determine that domain.
   * @param {string} destination – The destination specification.
   *   This is made up of one or two components, split by a hash or /
   *   sign ('# | /'); The first is the subdomain we're
   *   redirecting two (gate, core, dashboard, ...); the
   *   second is optional and is the route inside the other module.
   *   Based on the divider # or / it will route to an angular routing if it's an #
   *   And it will route to a normal route if it's divided by a /
   * @return {string}
   * calculate a deep link to a sibling domain
   */
  link(destination: string): string {
    if (!angular.isString(destination)) {
      throw new Error('destination should be a string');
    }

    if (destination.length === 0) {
      throw new Error('destination should have no-zero length');
    }

    if (
      destination.startsWith('http://') ||
      destination.startsWith('https://')
    ) {
      return destination;
    }

    // regex allows <any letters, number or _>[#<any chars>]
    const match = destination.match(/^([^#\/\n@]+)(?:([#\/])(.*))?$/);
    if (match.length !== 4) {
      throw new Error(
        'Invalid inter-domain link ' +
          'specification "' +
          destination +
          '". Links must have ' +
          'the form "$subdomain[/$path#$hashPath]", where subdomain ' +
          'is the service you are linking to (e.g. gate, ' +
          'dashboard, …) and $path is the route on that ' +
          'service, and $hashPath is deep link in the application',
      );
    }

    const routingType = match[2] || '#';
    const domain = match[1];
    const path = match[3] || '';
    let url = '';

    if (!path) {
      url = routingType === '#' ? '#/' : '/';
    } else {
      url = (routingType === '#' ? '#' : '/') + path;
    }

    if (domain === 'softwear6' || domain === 'spynl-services') {
      return this.sibling(domain, true) + url;
    }

    return this.sibling('www', true) + `/${domain}.html${url}`;
  }
}
